import { collectMentions } from "@corca/tiptap";
import { uniqBy } from "es-toolkit";
import { makeAutoObservable, runInAction } from "mobx";
import { nanoid } from "nanoid";
import * as Y from "yjs";

import { getChat, getApiErrorMessage, markChatAsRead, stopChatReply, updateChat } from "@/api";
import { onCableMessage, onCableConnect, onCableLost, subscribe } from "@/api/cable";
import { isChatInputEmpty } from "@/models/chat/chatHelpers";
import { DocumentModel } from "@/models/DocumentModel";
import { uint8ArrayToBase64 } from "@/utils/base64/uint8ArrayToBase64";
import { getImageDimensions } from "@/utils/getImageDimensions";
import { trackEvent } from "@/utils/trackEvent";
import { uploadFile } from "@/utils/uploadFile";

import { CHAT_ICON_META_KEY, chatIconName } from "./chatIcons";
import { ChatMessageModel, STREAM_STATE_META_KEY, STREAMING_STATE } from "./ChatMessageModel";
import { CHAT_MODEL_META_KEY, DEFAULT_CHAT_MODEL_ID, getChatModelOption } from "./chatModels";

import type { ChatIconName } from "./chatIcons";
import type { ChatModelId, ChatModelOption } from "./chatModels";
import type { Chat, Message, Upload, UserShort } from "@/api";
import type { ChatsStore } from "@/store/ChatsStore";
import type { UserData } from "@/store/user/userTypes";
import type { EventMap } from "@/utils/trackEvent";
import type { CollectedMention } from "@corca/tiptap";

const INPUT_DOC_ID = "chatInputDocId";

// Keep in sync with apps/chat-bot/src/openai/sendReplyMessage.ts
const ALLOW_WRITE_TO_DOCUMENT_META_KEY = "allowWriteToDocument";
const STOP_REQUESTED_META_KEY = "stopRequested";
const COMPLETE_STATE = "complete";

type LocalMessage = { message: Message; mentions: CollectedMention[] };

export type ChatInputFileStatus = "uploading" | "uploaded" | "failed";

export type ChatInputFile = {
  id: string;
  status: ChatInputFileStatus;
  key?: string;
  file: File;
  width?: number;
  height?: number;
  progress: number;
};

export class ChatModel {
  docId: string;

  private chatData: Chat;

  private messagesMap = new Map<string, ChatMessageModel>();

  private cableUnsubscribe?: () => void;

  private cableConnectUnsubscribe?: () => void;

  private cableLostUnsubscribe?: () => void;

  private cableChannel?: ReturnType<typeof subscribe>;

  /**
   * Last cursor that was synced before websocket disconnect. After reconnect we need to fetch
   * all messages after this cursor.
   */
  private lastSyncCursor?: string;

  inputDoc: DocumentModel;

  chatRef: React.RefObject<HTMLUListElement | null> | null = null;

  fetchingPreviousMessages = false;

  fetchingPreviousMessageError = "";

  allMessagesLoaded = false;

  allowBotWriteToDocument = true;

  allowBotWriteToDocumentSyncError = false;

  selectedBotModelId: ChatModelId = DEFAULT_CHAT_MODEL_ID;

  selectedBotModelSyncError = false;

  private localStorageKey: string;

  isChatSettingsSyncInProgress = false;

  private chatSettingsSyncDispose?: () => void;

  private chatSettingsSyncVersion = 0;

  private botStopRequested = false;

  userData?: UserData | null;

  isOpened = false;

  // The last message that arrived over the live connection; history loads never set it
  lastLiveMessageUid = "";

  inputFiles: ChatInputFile[] = [];

  private readonly uploadControllers = new Map<string, AbortController>();

  readonly chatsStore?: ChatsStore;

  pendingScrollToMessageUid?: string;

  constructor(chatData: Chat, docId: string, userData?: UserData | null, chatsStore?: ChatsStore) {
    this.chatData = chatData;
    // TODO: we should probably add it to the Chat type from OpenAPI
    this.docId = docId;
    this.chatsStore = chatsStore;
    this.resetChatSettingsFromMeta();
    this.updateMessages(this.chatData.messages);

    // Restore failed messages
    this.localStorageKey = ChatModel.getLocalStorageKey(this.uid);
    this.localMessages.forEach(({ message }) => {
      const m = new ChatMessageModel(message, this, "failed");
      this.messagesMap.set(m.uid, m);
    });

    this.inputDoc = new DocumentModel(`${INPUT_DOC_ID}:${chatData.uid}`);
    this.inputDoc.onSubmit = () => {
      this.sendMessage("shortcut").catch((err) => {
        console.error("Failed to send chat message:", err);
      });
    };
    this.userData = userData;

    makeAutoObservable<ChatModel, "uploadControllers">(this, { uploadControllers: false });
  }

  setIsOpened(isOpened: boolean): void {
    this.isOpened = isOpened;
  }

  setPendingScrollToMessageUid(uid?: string) {
    this.pendingScrollToMessageUid = uid;
  }

  get withBot(): boolean {
    return this.chatData.kind === "personal";
  }

  get isCollective(): boolean {
    return this.chatData.kind === "collective";
  }

  get isDirect(): boolean {
    return this.chatData.kind === "direct";
  }

  /** The subscriber of a direct chat who isn't the current user, or the current user in a self chat. */
  get otherParticipant(): UserShort | undefined {
    if (!this.isDirect) return undefined;
    const { subscribers } = this.chatData;
    return subscribers.find((user) => user.uid !== this.userData?.uid) ?? subscribers[0];
  }

  /** The other participant's name for a direct chat, the chat title otherwise. */
  get displayTitle(): string | null | undefined {
    return this.isDirect ? this.otherParticipant?.name : this.title;
  }

  get accessTeamUid(): string | undefined {
    if (this.docId || this.withBot) return undefined;

    return this.chatData.teamUid ?? undefined;
  }

  get isBotResponding(): boolean {
    const meta = this.latestBotReplyMeta;
    return (
      this.withBot &&
      !this.botStopRequested &&
      meta?.[STREAM_STATE_META_KEY] === STREAMING_STATE &&
      meta[STOP_REQUESTED_META_KEY] !== "true"
    );
  }

  get uid(): string {
    return this.chatData.uid;
  }

  get title(): string | null | undefined {
    return this.chatData.title;
  }

  get localMessages(): LocalMessage[] {
    return JSON.parse(localStorage.getItem(this.localStorageKey) ?? "[]") as LocalMessage[];
  }

  get messagesCount(): number {
    return this.chatData.messagesCount;
  }

  get unreadMessagesCount(): number {
    return this.chatData.unreadMessagesCount;
  }

  get icon(): ChatIconName {
    return chatIconName(this.chatData);
  }

  get messages(): ChatMessageModel[] {
    const messages = [
      ...Array.from(this.messagesMap.values()).sort((a, b) => {
        return a.cursor.localeCompare(b.cursor);
      }),
    ];
    return uniqBy(messages, (message) => message.uid);
  }

  get selectedBotModel(): ChatModelOption {
    return getChatModelOption(this.selectedBotModelId);
  }

  get lastActivityCursor(): string {
    return this.messages[this.messages.length - 1]?.cursor ?? this.chatData.createdAt;
  }

  async updateTitle(title: string): Promise<void> {
    runInAction(() => {
      this.chatData.title = title;
    });
    await updateChat(this.uid, { title });
  }

  // The API replaces meta wholesale, so the other keys must be sent along.
  async updateIcon(icon: ChatIconName): Promise<void> {
    const previousMeta = this.chatData.meta;
    const meta = { ...(previousMeta ?? {}), [CHAT_ICON_META_KEY]: icon };
    runInAction(() => {
      this.chatData.meta = meta;
    });
    try {
      const { chat } = await updateChat(this.uid, { meta });
      runInAction(() => {
        this.chatData.meta = chat?.meta ?? meta;
      });
    } catch (err) {
      runInAction(() => {
        this.chatData.meta = previousMeta;
      });
      throw err;
    }
  }

  async markAsRead(cursor?: string): Promise<void> {
    runInAction(() => {
      this.chatData.unreadMessagesCount = 0;
    });
    await markChatAsRead(this.uid, {
      cursor: cursor ?? this.messages[this.messages.length - 1]?.cursor,
    });
  }

  private createLocalMessage(message: Message, mentions: CollectedMention[]): void {
    const messages = this.localMessages;
    messages.push({ message, mentions });
    localStorage.setItem(this.localStorageKey, JSON.stringify(messages));
  }

  removeLocalMessage(uid: string, removeFromList = false) {
    const updatedMessages = this.localMessages.filter(({ message }) => message.uid !== uid);
    localStorage.setItem(this.localStorageKey, JSON.stringify(updatedMessages));
    if (removeFromList) {
      this.messagesMap.get(uid)?.dispose();
      this.messagesMap.delete(uid);
    }
  }

  static getLocalStorageKey(uid: string): string {
    return `chat-${uid}-messages-v2`;
  }

  static clearLocalMessages(uid: string): void {
    localStorage.removeItem(ChatModel.getLocalStorageKey(uid));
  }

  resendLocalMessage(uid: string): void {
    const stored = this.localMessages.find(({ message }) => message.uid === uid);
    if (!stored?.message.body) return;

    this.removeLocalMessage(uid, true);
    this.createMessage(stored.message.body, stored.mentions);
  }

  async createMessage(
    body: string,
    mentions: CollectedMention[],
    fileKeys?: string[],
    optimisticFiles?: Upload[],
    meta?: Record<string, string>,
  ): Promise<void> {
    const message = await ChatMessageModel.create(
      body,
      mentions,
      this,
      fileKeys,
      optimisticFiles,
      (_data, optimisticData) => {
        if (optimisticData.message) {
          this.removeLocalMessage(optimisticData.message.uid);
        }
      },
      meta,
    );
    if (message) {
      this.createLocalMessage(message.toJSON(), mentions);
      runInAction(() => {
        this.messagesMap.set(message.uid, message);
      });
    }
  }

  get uploadedFiles(): (ChatInputFile & { key: string })[] {
    return this.inputFiles.filter(
      (file): file is ChatInputFile & { key: string } => file.status === "uploaded",
    );
  }

  get isUploadingFiles(): boolean {
    return this.inputFiles.some((file) => file.status === "uploading");
  }

  get hasFailedFiles(): boolean {
    return this.inputFiles.some((file) => file.status === "failed");
  }

  async uploadFiles(files: FileList | File[]): Promise<void> {
    const selected = Array.from(files);
    const entries: ChatInputFile[] = selected.map((file) => ({
      id: nanoid(),
      status: "uploading",
      file,
      progress: 0,
    }));

    runInAction(() => {
      this.inputFiles.push(...entries);
    });

    await Promise.all(
      selected.map(async (file, index) => {
        const { id } = entries[index];
        const dimensionsPromise = getImageDimensions(file).then((dimensions) => {
          if (dimensions) {
            this.updateInputFile(id, dimensions);
          }
        });
        const controller = new AbortController();
        this.uploadControllers.set(id, controller);
        try {
          const key = await uploadFile(file, {
            signal: controller.signal,
            onProgress: (progress) => this.updateInputFile(id, { progress }),
          });
          this.updateInputFile(id, { status: "uploaded", key, progress: 1 });
        } catch (err) {
          if (controller.signal.aborted) return;
          console.error("Failed to upload chat file:", err);
          this.updateInputFile(id, { status: "failed" });
        } finally {
          this.uploadControllers.delete(id);
          await dimensionsPromise;
        }
      }),
    );
  }

  private abortUpload(id: string): void {
    const controller = this.uploadControllers.get(id);
    if (controller) {
      controller.abort();
      this.uploadControllers.delete(id);
    }
  }

  private updateInputFile(id: string, patch: Partial<ChatInputFile>): void {
    runInAction(() => {
      const file = this.inputFiles.find((f) => f.id === id);
      if (file) {
        Object.assign(file, patch);
      }
    });
  }

  removeInputFile(id: string): void {
    this.abortUpload(id);
    this.inputFiles = this.inputFiles.filter((file) => file.id !== id);
  }

  clearInputFiles(): void {
    for (const id of [...this.uploadControllers.keys()]) {
      this.abortUpload(id);
    }
    this.inputFiles = [];
  }

  setAllowBotWriteToDocument(value: boolean): void {
    this.allowBotWriteToDocument = value;
    this.allowBotWriteToDocumentSyncError = false;
    this.startChatSettingsSync();
  }

  private getAllowBotWriteToDocumentFromMeta(meta?: Record<string, string>): boolean {
    return meta?.[ALLOW_WRITE_TO_DOCUMENT_META_KEY] !== "false";
  }

  setSelectedBotModelId(value: ChatModelId): void {
    this.selectedBotModelId = value;
    this.selectedBotModelSyncError = false;
    this.startChatSettingsSync();
  }

  private getSelectedBotModelIdFromMeta(meta?: Record<string, string>): ChatModelId {
    return getChatModelOption(meta?.[CHAT_MODEL_META_KEY]).id;
  }

  private getChatSettingsMeta(): Record<string, string> {
    return {
      ...(this.chatData.meta ?? {}),
      [ALLOW_WRITE_TO_DOCUMENT_META_KEY]: this.allowBotWriteToDocument ? "true" : "false",
      [CHAT_MODEL_META_KEY]: this.selectedBotModelId,
    };
  }

  private async startChatSettingsSync(): Promise<void> {
    this.isChatSettingsSyncInProgress = true;
    await this.syncChatSettings();
    runInAction(() => {
      this.isChatSettingsSyncInProgress = false;
    });
  }

  private resetChatSettingsFromMeta(): void {
    this.allowBotWriteToDocument = this.getAllowBotWriteToDocumentFromMeta(this.chatData.meta);
    this.allowBotWriteToDocumentSyncError = false;
    this.selectedBotModelId = this.getSelectedBotModelIdFromMeta(this.chatData.meta);
    this.selectedBotModelSyncError = false;
  }

  private restoreChatSettingsFromMetaAfterSyncError(nextMeta: Record<string, string>): void {
    const persistedAllowBotWriteToDocument = this.getAllowBotWriteToDocumentFromMeta(
      this.chatData.meta,
    );
    const persistedSelectedBotModelId = this.getSelectedBotModelIdFromMeta(this.chatData.meta);

    this.allowBotWriteToDocument = persistedAllowBotWriteToDocument;
    this.selectedBotModelId = persistedSelectedBotModelId;
    this.allowBotWriteToDocumentSyncError =
      nextMeta[ALLOW_WRITE_TO_DOCUMENT_META_KEY] !==
      (persistedAllowBotWriteToDocument ? "true" : "false");
    this.selectedBotModelSyncError = nextMeta[CHAT_MODEL_META_KEY] !== persistedSelectedBotModelId;
  }

  private async syncChatSettings(): Promise<boolean> {
    if (!this.withBot) {
      return true;
    }

    const nextMeta = this.getChatSettingsMeta();
    this.chatSettingsSyncDispose?.();

    const abortController = new AbortController();
    const syncVersion = this.chatSettingsSyncVersion + 1;
    this.chatSettingsSyncVersion = syncVersion;
    this.chatSettingsSyncDispose = () => {
      abortController.abort("Chat settings sync disposed");
    };

    try {
      const { chat } = await updateChat(
        this.uid,
        { meta: nextMeta },
        { signal: abortController.signal },
      );
      if (abortController.signal.aborted || syncVersion !== this.chatSettingsSyncVersion) {
        return true;
      }

      runInAction(() => {
        this.chatData.meta = chat?.meta ?? nextMeta;
        this.resetChatSettingsFromMeta();
      });
      return true;
    } catch (_err) {
      if (abortController.signal.aborted || syncVersion !== this.chatSettingsSyncVersion) {
        return true;
      }

      runInAction(() => {
        this.restoreChatSettingsFromMetaAfterSyncError(nextMeta);
      });
      return false;
    } finally {
      if (syncVersion === this.chatSettingsSyncVersion) {
        this.chatSettingsSyncDispose = undefined;
      }
    }
  }

  setFetchingPreviousMessages(fetching: boolean): void {
    this.fetchingPreviousMessages = fetching;
  }

  setAllMessagesLoaded(loaded: boolean): void {
    this.allMessagesLoaded = loaded;
  }

  setFetchingPreviousMessageError(error: string): void {
    this.fetchingPreviousMessageError = error;
  }

  async fetchMessages(cursor?: string): Promise<Message[] | undefined> {
    try {
      const { chat } = await getChat(this.uid, { cursor });
      if (chat) {
        runInAction(() => {
          this.chatData.meta = chat.meta;
          this.resetChatSettingsFromMeta();
        });
        this.updateMessages(chat.messages ?? []);
        return chat.messages;
      }
    } catch (err) {
      console.error("Error fetching messages: ", err);
    }
  }

  async fetchAllMissingMessages(cursor?: string, callCount: number = 0): Promise<void> {
    if (callCount >= 5 || !this.lastSyncCursor) {
      this.lastSyncCursor = undefined;
      return;
    }
    try {
      const messages = await this.fetchMessages(cursor);
      if (!messages || messages.length === 0) {
        return;
      }
      const minCursor = messages.reduce((min, m) => {
        return m.cursor < min ? m.cursor : min;
      }, messages[0]?.cursor ?? "");

      if (minCursor > this.lastSyncCursor) {
        this.fetchAllMissingMessages(minCursor, callCount + 1);
      } else {
        this.lastSyncCursor = undefined;
      }
    } catch (err) {
      console.error("Error fetching all missing messages: ", err);
    }
  }

  async fetchPreviousMessages(): Promise<void> {
    const firstMessageCursor = this.messages[0]?.cursor ?? 0;
    if (this.fetchingPreviousMessages || this.allMessagesLoaded) {
      return;
    }
    try {
      this.setFetchingPreviousMessages(true);
      const { chat } = await getChat(this.uid, {
        cursor: firstMessageCursor,
      });
      const newFirstMessageCursor =
        chat?.messages.sort((a, b) => a.cursor.localeCompare(b.cursor))[0].cursor ?? "";
      this.updateMessages(chat?.messages ?? []);
      this.setFetchingPreviousMessages(false);
      if (firstMessageCursor === newFirstMessageCursor) {
        this.setAllMessagesLoaded(true);
      }
    } catch (err) {
      this.setFetchingPreviousMessages(false);
      this.setFetchingPreviousMessageError(getApiErrorMessage(err));
    }
  }

  private updateMessages(messages: Message[]): void {
    messages.forEach((message) => {
      this.updateMessage(message);
    });
  }

  private updateMessage(message: Message) {
    const existingMessage = this.messagesMap.get(message.uid);
    if (existingMessage) {
      existingMessage.setMessageData(message);
    } else if (!existingMessage) {
      this.messagesMap.set(message.uid, new ChatMessageModel(message, this));
    }
  }

  private updateMessageUid(fromUid: string, toUid: string): boolean {
    const item = this.messagesMap.get(fromUid);
    if (item) {
      this.messagesMap.delete(fromUid);
      this.messagesMap.set(toUid, item);
      return true;
    }
    return false;
  }

  subscribe(): void {
    this.cableChannel = subscribe("ChatChannel", { chat_id: this.uid });
    this.cableUnsubscribe = onCableMessage((payload) => {
      if (payload.type === "message_created" && payload.chat_id === this.uid) {
        // A message can arrive twice: after a cable reconnect,
        // fetchAllMissingMessages may add it before its broadcast is delivered.
        const isKnownMessage = this.messagesMap.has(payload.message.uid);
        const clientMessageUid = payload.client_message_uid;
        const isOwnMessage = !!clientMessageUid;
        if (clientMessageUid) {
          this.updateMessageUid(clientMessageUid, payload.message.uid);
        }
        this.updateMessage(payload.message);
        if (isKnownMessage) return;

        runInAction(() => {
          this.lastLiveMessageUid = payload.message.uid;
          this.chatData.messagesCount += 1;
          if (!this.isOpened && !isOwnMessage) {
            this.chatData.unreadMessagesCount += 1;
          }
        });
        if (this.isOpened) {
          this.markAsRead();
        }
      }

      if (payload.type === "message_updated" && payload.chat_id === this.uid) {
        this.updateMessage(payload.message);
      }

      if ("message_uid" in payload && "reaction" in payload && payload.reaction) {
        this.messagesMap.get(payload.message_uid)?.applyReaction(payload.reaction);
      }
    });
    this.cableConnectUnsubscribe = onCableConnect(async () => {
      await this.fetchAllMissingMessages();
      if (this.isOpened) {
        this.markAsRead();
      }
    });
    this.cableLostUnsubscribe = onCableLost(() => {
      if (!this.lastSyncCursor && this.messages.length > 0) {
        this.lastSyncCursor = this.messages[this.messages.length - 1].cursor;
      }
    });
  }

  dispose(): void {
    this.cableChannel?.disconnect();
    this.cableUnsubscribe?.();
    this.cableConnectUnsubscribe?.();
    this.cableLostUnsubscribe?.();
    this.chatSettingsSyncDispose?.();
    return [...this.messagesMap.values()].forEach((message) => {
      message.dispose();
    });
  }

  private get latestBotReplyMeta(): Record<string, string> | undefined {
    const { messages } = this;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const meta = messages[i]?.messageData.meta;
      const streamState = meta?.[STREAM_STATE_META_KEY];
      if (streamState === STREAMING_STATE || streamState === COMPLETE_STATE) {
        return meta;
      }
    }

    return undefined;
  }

  requestBotStop = async (): Promise<void> => {
    if (!this.withBot || this.botStopRequested) {
      return;
    }

    this.botStopRequested = true;
    try {
      await stopChatReply(this.uid, { skipCache: true });
    } catch (err) {
      runInAction(() => {
        this.botStopRequested = false;
      });
      throw err;
    }
  };

  sendMessage = async (
    source: EventMap["chat_message_sent"]["source"],
    inputDoc: DocumentModel = this.inputDoc,
    meta?: Record<string, string>,
  ): Promise<boolean> => {
    if (!inputDoc.yDoc) {
      return false;
    }

    const { tiptap } = inputDoc.editor;
    const hasText = !isChatInputEmpty(tiptap);
    const { uploadedFiles } = this;
    if (this.isUploadingFiles || this.hasFailedFiles || (!hasText && uploadedFiles.length === 0)) {
      return false;
    }

    if (
      this.withBot &&
      (this.allowBotWriteToDocumentSyncError ||
        this.selectedBotModelSyncError ||
        this.isChatSettingsSyncInProgress)
    ) {
      return false;
    }

    const fileKeys = uploadedFiles.map((file) => file.key);
    const optimisticFiles: Upload[] = uploadedFiles.map(({ key, file, width, height }) => ({
      typename: "Upload",
      key,
      filename: file.name,
      contentType: file.type,
      byteSize: file.size,
      signedUrl: URL.createObjectURL(file),
      width,
      height,
    }));
    const body = hasText ? uint8ArrayToBase64(Y.encodeStateAsUpdate(inputDoc.yDoc)) : "";
    const mentions = hasText && tiptap ? collectMentions(tiptap.state.doc) : [];
    this.clearInputFiles();
    inputDoc.clear();

    if (this.chatsStore && !this.chatsStore.isOpenChat) {
      this.chatsStore.openChat(this.withBot, this.uid);
    }
    trackEvent("chat_message_sent", {
      source,
      chat_with_bot: this.withBot,
    });
    await this.createMessage(
      body,
      mentions,
      fileKeys.length > 0 ? fileKeys : undefined,
      optimisticFiles.length > 0 ? optimisticFiles : undefined,
      meta,
    );

    if (this.isOpened) {
      this.markAsRead();
    }
    this.scrollToBottom();
    runInAction(() => {
      if (this.withBot) {
        this.botStopRequested = false;
      }
    });

    return true;
  };

  scrollToBottom() {
    if (this.chatRef?.current) {
      this.chatRef.current.scrollTop = this.chatRef.current.scrollHeight;
    }
  }

  hasMessage(uid: string): boolean {
    return this.messagesMap.has(uid);
  }

  indexOfMessage(uid: string): number {
    return this.messages.findIndex((m) => m.uid === uid);
  }
}
